// Bill Runs' "Export MultiSite XLSX": one row per bill in the column layout of
// the MultiSite Bill Validation Detail Report the client already uses
// (docs/inputs/MultiSite Bill Validation Detail Report-*.xlsx), plus a Summary
// sheet of headline metrics. Bill Runs picks the bills (months, mode, filters)
// and passes each once; Period end mode matches the client's file, where a
// 21 May – 19 Jun bill is a June bill.

import type { BvBill, ShadowBillSection } from '../types/billTypes';
import type { BillReport } from './billVerdict';
import { effectiveInvoiceResult } from './effectiveInvoice';
import { MANUAL_NOTES } from './reportFormulas';

export type MatchStatus = 'Passed' | 'Mismatched' | 'Awaiting Invoice' | 'Not Calculated';

const METERING_PROVIDERS: Record<string, string> = { mondo: 'Mondo Power', powermetric: 'Powermetric' };
const REVIEW_STEPS = ['internal_review', 'system_review'];

const round2 = (n: number) => Math.round(n * 100) / 100;

export function matchStatus(bill: Partial<BvBill>, report: BillReport | null): MatchStatus {
  if (!effectiveInvoiceResult(bill)) return 'Awaiting Invoice';
  if (!report) return 'Not Calculated';
  return report.verdict.bucket === 'within_range' ? 'Passed' : 'Mismatched';
}

/** Processing status, in the client file's vocabulary where it has one. */
export function processStatus(bill: Partial<BvBill>, report: BillReport | null): string {
  if (bill.bill_run_status === 'error') return 'Error';
  if (bill.workflow_status && REVIEW_STEPS.includes(bill.workflow_status)) return 'On Hold';
  return report ? 'Released' : 'Pending';
}

/** Invoice − expected for one section; 0 when the section is not on the report. */
function sectionDiff(report: BillReport | null, key: ShadowBillSection): number {
  const s = report?.sections.find((r) => r.key === key);
  return s ? round2(s.actual - s.shadow) : 0;
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');

/** One row of the detail sheet, keyed by the client file's column headers. */
export function detailRow(bill: Partial<BvBill>, report: BillReport | null): Record<string, unknown> {
  const site = bill.site;
  const extn = site?.bv_extn ?? {};
  const ar = effectiveInvoiceResult(bill);
  const note = bill.report_data?.customer_note_manual
    || (bill.report_data?.customer_note_type ? MANUAL_NOTES[bill.report_data.customer_note_type] : '');
  return {
    'Organization': site?.client?.name ?? '',
    'Country': 'AU',
    'Market': 'AU-NEM',
    'Commodity': 'Electricity',
    'Customer Class': 'LargeBusiness',
    'Service Point': site?.nmi ?? '',
    'Service Point Type': 'NMI',
    'State': site?.addr_state ?? '',
    'Postcode': site?.addr_postcode ?? '',
    'Retailer': site?.current_retailer || ar?.extracted_retailer || '',
    'Distributor': str(extn.network_distributor) || ar?.extracted_distributor || '',
    'Distribution Products': str(extn.network_tariff_code),
    'Metering Provider': METERING_PROVIDERS[str(extn.metering_provider)] ?? '',
    'Account Number': ar?.extracted_account_number ?? '',
    'Period Start': bill.bill_from ?? '',
    'Period End': bill.bill_to ?? '',
    'Issue Date': ar?.extracted_invoice_date ?? '',
    'Status': processStatus(bill, report),
    'Status Notes': bill.bill_run_error || ar?.match_warning || '',
    'Match Status': matchStatus(bill, report),
    'Is Manually Matched': bill.report_data?.validation_status === 'validated_approved' ? 'Y' : 'N',
    'Customer Note': note,
    'Retail Difference': sectionDiff(report, 'retail'),
    'Environmental Difference': sectionDiff(report, 'environmental'),
    'Metering Difference': sectionDiff(report, 'metering'),
    'Distribution Difference': sectionDiff(report, 'distribution'),
    // Charges on the invoice we do not validate (charge-map pass-throughs).
    'Uncontracted Charges': ar && report ? round2(ar.subtotal - report.subtotal.invoice) : '',
    'Total Difference': report ? report.verdict.variance : '',
    'Acquired On': ar?.parsed_at?.slice(0, 10) ?? '',
    'Billed Usage': bill.total_kwh ?? ar?.extracted_total_kwh ?? '',
    'Usage Unit': 'kWh',
    'Currency': 'AUD',
    'Opening Balance': ar?.extracted_previous_balance ?? '',
    'New Charges ex. Tax': ar?.subtotal ?? '',
    'Tax on New Charges': ar?.gst ?? '',
    'New Charges Inc. Tax': ar?.total ?? '',
    'Closing Balance': ar?.extracted_total_due ?? '',
    'Due Date': ar?.extracted_due_date ?? '',
    'Additional Notes': bill.notes ?? '',
  };
}

export type MonthMetrics = {
  bills: number;
  sites: number;
  passed: number;
  mismatched: number;
  overcharged: number;
  undercharged: number;
  awaitingInvoice: number;
  notCalculated: number;
  /** Passed ÷ bills that have a verdict, 0–100. Null with none. */
  passRate: number | null;
  invoicedIncTax: number;
  billedKwh: number;
};

export function monthMetrics(rows: { bill: Partial<BvBill>; report: BillReport | null }[]): MonthMetrics {
  const m: MonthMetrics = {
    bills: rows.length, sites: new Set(rows.map((r) => r.bill.site_id)).size,
    passed: 0, mismatched: 0, overcharged: 0, undercharged: 0, awaitingInvoice: 0, notCalculated: 0,
    passRate: null, invoicedIncTax: 0, billedKwh: 0,
  };
  for (const { bill, report } of rows) {
    const status = matchStatus(bill, report);
    if (status === 'Passed') m.passed++;
    else if (status === 'Mismatched') m.mismatched++;
    else if (status === 'Awaiting Invoice') m.awaitingInvoice++;
    else m.notCalculated++;
    // Counts only: variance is read per bill on the Multiple Sites sheet, never totalled.
    if (report?.verdict.bucket === 'overcharge') m.overcharged++;
    if (report?.verdict.bucket === 'undercharge') m.undercharged++;
    m.invoicedIncTax += effectiveInvoiceResult(bill)?.total ?? 0;
    m.billedKwh += bill.total_kwh ?? 0;
  }
  const judged = m.passed + m.mismatched;
  m.passRate = judged ? Math.round((m.passed / judged) * 1000) / 10 : null;
  m.invoicedIncTax = round2(m.invoicedIncTax);
  m.billedKwh = Math.round(m.billedKwh);
  return m;
}

/**
 * The Summary sheet: one label/value row per metric. `coverage` (Bill Runs'
 * coverage totals) adds the Dashboard's site counts, so the two reconcile.
 */
export function summaryRows(
  monthLabel: string,
  m: MonthMetrics,
  coverage?: { expected: number; hasBill: number; missing: number },
): Record<string, unknown>[] {
  return [
    ['Month', monthLabel],
    ...(coverage ? [
      ['Expected sites', coverage.expected],
      ['Sites with a bill', coverage.hasBill],
      ['No bill yet', coverage.missing],
    ] : []),
    ['Bills', m.bills],
    ['Sites', m.sites],
    ['Passed', m.passed],
    ['Mismatched', m.mismatched],
    ['  Overcharged', m.overcharged],
    ['  Undercharged', m.undercharged],
    ['Awaiting Invoice', m.awaitingInvoice],
    ['Not Calculated', m.notCalculated],
    ['Pass Rate %', m.passRate ?? ''],
    ['Invoiced Inc. Tax ($)', m.invoicedIncTax],
    ['Billed Usage (kWh)', m.billedKwh],
  ].map(([Metric, Value]) => ({ Metric, Value }));
}
